import { IFailedRequest } from "./login.services";
import { request } from "./utils";

export const dashboardServices = {
  getSubscriptionInfo,
  getUpcomingInvoice,
  submitInvoice,
  getBillingManagers,
  deleteBillingManager,
  createBillingManager,
};

export interface ISubscriptionData {
  customer_id: string;
  subscription_id: string;
  start_date: number;
  subscription_items: ISubscriptionItemData[];
  primary_email?: string;
  is_primary_user?: boolean;
}

export interface ISubscriptionItemData {
  amount: number;
  id: string;
  new_item: boolean;
  quantity: number;
  product_id: string;
  product: IProduct;
}

export interface IProduct {
  id: string;
  description: string | null;
  name: string;
  price: string;
  country: string;
}

export interface IInvoiceData {
  customer_id: string;
  subscription_id: string;
  subscription_items: {
    id: string;
    quantity: number;
    new_item: boolean;
    price: string;
  }[];
}

export interface INewInvoiceData {
  amount_due: string;
  period_start: number;
  period_end: number;
  customer_id: string;
  subscription_id: string;
  subscription_items: {
    id: string;
    current_quantity: number;
    current_amount: number;
    new_quantity: number;
    new_amount: number;
    product_id: string;
    price: string;
    new_item: boolean;
    base_amount: number;
  }[];
  base_items: {
    id: string;
    amount: number;
    quantity: number;
    product_id: string;
    base_amount: number;
  }[];
}

export interface ISubmittedInvoiceResponse {
  id: string;
  customer_id: string;
  quantity: number;
  subscription_id: string;
  unit_amount: number;
}

export interface IScheduleEnabledSettings {
  community_id: string | undefined;
  is_enabled: boolean;
}

export interface IBillingManagerData {
  name: string;
  primary_email: string;
  time_created: string;
  user_email: string;
}

/**
 * Get a user's subscription information
 *
 * @param {string} email data
 */
async function getSubscriptionInfo(
  email: string
): Promise<ISubscriptionData[]> {
  const requestOptions: RequestInit = {
    method: "GET",
  };
  const data = await request(
    `/admin/stripe/subscriptions?email=${encodeURIComponent(email)}`,
    requestOptions,
    true
  );
  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  }

  return data;
}

/**
 * Get a user's upcoming invoice information
 *
 */
async function getUpcomingInvoice(
  invoice: IInvoiceData[]
): Promise<INewInvoiceData[]> {
  const requestOptions: RequestInit = {
    method: "POST",
    body: JSON.stringify(invoice),
  };
  const data = await request(`/admin/stripe/invoice`, requestOptions, true);

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  }

  return data;
}

/**
 * Submit a user's upcoming invoice information
 *
 */
async function submitInvoice(
  invoice: IInvoiceData[]
): Promise<ISubscriptionData[]> {
  const requestOptions: RequestInit = {
    method: "POST",
    body: JSON.stringify(invoice),
  };
  const data = await request(
    `/admin/stripe/subscription_items`,
    requestOptions,
    true
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  }

  return data;
}

/**
 * Get a user's billing managers
 *
 */
async function getBillingManagers(): Promise<IBillingManagerData[]> {
  const requestOptions: RequestInit = {
    method: "GET",
  };
  const data = await request(`/billing_roles`, requestOptions, true);
  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  }

  return data;
}

/**
 * Submit a new billing manager for a user
 *
 */
async function createBillingManager(
  email: string
): Promise<IBillingManagerData[]> {
  const requestOptions: RequestInit = {
    method: "POST",
    body: JSON.stringify({
      user_email: email,
      name: "admin",
    }),
  };
  const data = await request(`/billing_roles`, requestOptions, true);

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  }

  return Promise.resolve(data as IBillingManagerData[]);
}

/**
 * Delete a user's billing manager by email
 *
 * @param {string} email data
 */
async function deleteBillingManager(
  email: string
): Promise<ISubscriptionData[]> {
  const requestOptions: RequestInit = {
    method: "DELETE",
  };
  const data = await request(
    `/billing_roles?user_email=${encodeURIComponent(email)}`,
    requestOptions,
    true
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  }

  return data;
}
